import React, { useState, useEffect } from 'react';
import {
  Tabs,
  Tab,
  TabPanel,
  TabPanels,
  Box,
  useColorModeValue,
  Heading,
  Divider,
  Icon,
  VStack,
  Breadcrumb,
  BreadcrumbItem,
  Text,
  Flex,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { LuLogs } from 'react-icons/lu';
import {
  FaChevronLeft,
  FaChevronRight,
  FaEthernet,
  FaSlidersH,
  FaCodeBranch,
  FaChartLine,
  FaPlusCircle,
  FaLayerGroup,
  FaHeartbeat,
} from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';

import Interface from '../interface';
import Configure from '../configure';
import Logic from '../logic';
import TelemetryMonitor from '../monitor';
import Logs from '../logs';
import Logic2 from '../logic/Logic2';
import AddConfiguration from '../addConfiguration/AddConfiguration';
import DeviceTemplate from '../deviceTemplates/DeviceTemplate';
import DeviceHealth from '../deviceHealth/DeviceHealth';
import HealthCharts from '../deviceHealth/HealthCharts';

const EditDevice = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tabIndex, setTabIndex] = useState(location.state?.activeTab ?? 0);
  const [collapsed, setCollapsed] = useState(false);
  const deviceId = localStorage.getItem('deviceId');
  const [selectedDeviceForHealthCharts, setSelectedDeviceForHealthCharts] =
    useState(null);

  useEffect(() => {
    if (location?.state?.activeTab !== undefined) {
      setTabIndex(location.state.activeTab);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location?.state, navigate, location?.pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tabIndex]);

  const breadCrumBg = useColorModeValue('gray.200', 'navy.700');
  const activeTabBg = useColorModeValue('navy.50', 'navy.600');
  const iconBg = useColorModeValue('gray.50', 'navy.900');
  const sideBarBg = useColorModeValue('white', 'navy.700');
  const activeTabColor = useColorModeValue('gray.700', 'white');

  const sidebarExpandedWidth = '240px';
  const sidebarCollapsedWidth = '70px';
  const sidebarWidth = collapsed ? sidebarCollapsedWidth : sidebarExpandedWidth;
  const topOffset = '60px';

  const tabItems = [
    { label: 'Interfaces', icon: FaEthernet, color: 'teal.400' },
    { label: 'Configurations', icon: FaSlidersH, color: 'orange.400' },
    { label: 'Add Configuration', icon: FaPlusCircle, color: 'orange.400' },
    { label: 'Logics', icon: FaCodeBranch, color: 'purple.400' },
    { label: 'Logic2', icon: FaCodeBranch, color: 'purple.400' },
    { label: 'Monitoring', icon: FaChartLine, color: 'blue.400' },

    { label: 'Audit Logs', icon: LuLogs, color: 'red' },
    { label: 'Device Health', icon: FaHeartbeat, color: 'green.400' },
    { label: 'Health Charts', icon: FaHeartbeat, color: 'green.400' },
    // { label: 'Templates', icon: FaLayerGroup, color: 'cyan.400' },
  ];

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Edit Device', path: null },
    { label: deviceId, path: null },
  ];

  const getDisplayTabIndex = (actualIndex) => {
    if (actualIndex === 2) return 1;
    if (actualIndex === 4) return 3;
    if (actualIndex === 8) return 7;

    return actualIndex;
  };

  const displayTabIndex = getDisplayTabIndex(tabIndex);

  return (
    <Box minH="100vh" p={4} mt={2}>
      {/* Breadcrumb */}
      <Breadcrumb
        mb={6}
        fontWeight="bold"
        fontSize="sm"
        color={activeTabColor}
        backgroundColor={breadCrumBg}
        padding={2}
        borderRadius="md"
        width="fit-content"
        separator=">"
        ml={sidebarWidth}
        transition="margin-left 0.3s ease"
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          return (
            <BreadcrumbItem key={index} isCurrentPage={isLast}>
              {isLast ? (
                <Text color="gray.400" fontWeight="bold">
                  {item.label}
                </Text>
              ) : (
                <Text
                  cursor="pointer"
                  _hover={{ textDecoration: 'underline' }}
                  onClick={() => navigate(item.path)}
                >
                  {item.label}
                </Text>
              )}
            </BreadcrumbItem>
          );
        })}
      </Breadcrumb>

      <Tabs
        index={tabIndex}
        onChange={(index) => setTabIndex(index)}
        orientation="vertical"
        variant="unstyled"
        isLazy
      >
        {/* Sidebar */}
        <Card
          position="fixed"
          top={topOffset}
          padding={collapsed ? 1 : 3}
          left={0}
          width={sidebarWidth}
          height={`calc(100vh - ${topOffset})`}
          borderRadius="none"
          zIndex={10}
          bg={sideBarBg}
          transition="width 0.3s ease"
        >
          <Flex align="center" justify="space-between" mb={4} px={4} pt={2}>
            {!collapsed && <Heading size="md">Configure</Heading>}
            <Icon
              mt={collapsed ? 3 : 0}
              mx={collapsed ? 2 : 0}
              as={collapsed ? FaChevronRight : FaChevronLeft}
              onClick={() => setCollapsed(!collapsed)}
              cursor="pointer"
              boxSize={4}
            />
          </Flex>

          <Divider mb={4} />

          {/* Sidebar Tabs */}
          <VStack spacing={2} align="stretch" px={collapsed ? 1 : 2}>
            {tabItems.map((tab, i) => (
              <Tab
                key={i}
                style={
                  tab.label === 'Add Configuration' ||
                  tab.label === 'Logic2' ||
                  tab.label === 'Health Charts'
                    ? { display: 'none' }
                    : {}
                }
                aria-hidden={
                  tab.label === 'Add Configuration' ||
                  tab.label === 'Logic2' ||
                  tab.label === 'Health Charts'
                    ? 'true'
                    : 'false'
                }
                _selected={{ bg: activeTabBg, color: activeTabColor }}
                bg={
                  collapsed ? iconBg : displayTabIndex === i ? activeTabBg : ''
                }
                color={displayTabIndex === i ? activeTabColor : ''}
                borderRadius="md"
                py={3}
                px={2}
                justifyContent={collapsed ? 'center' : 'flex-start'}
                textAlign="left"
                display="flex"
                alignItems="center"
                gap={collapsed ? 0 : 3}
                fontWeight="medium"
                fontSize="md"
                cursor="pointer"
              >
                <Icon as={tab.icon} boxSize={5} color={tab.color} />
                {!collapsed &&
                  tab.label !== 'Add Configuration' &&
                  tab.label !== 'Logic2' &&
                  tab.label}
              </Tab>
            ))}
          </VStack>
        </Card>

        {/* Tab Content */}
        <Box
          ml={sidebarWidth}
          width={`calc(100% - ${sidebarWidth})`}
          minHeight="100vh"
          transition="margin-left 0.3s ease, width 0.3s ease"
        >
          <TabPanels height="100%">
            <TabPanel p={0}>
              <Interface />
            </TabPanel>
            <TabPanel p={0}>
              <Configure />
            </TabPanel>
            <TabPanel p={0}>
              <AddConfiguration />
            </TabPanel>
            <TabPanel p={0}>
              <Logic />
            </TabPanel>
            <TabPanel p={0}>
              <Logic2 />
            </TabPanel>
            <TabPanel p={0}>
              <TelemetryMonitor />
            </TabPanel>

            <TabPanel p={0}>
              <Logs />
            </TabPanel>
            <TabPanel p={0}>
              <HealthCharts
                inEditDevice
                deviceName={selectedDeviceForHealthCharts}
                onDeviceChange={setSelectedDeviceForHealthCharts}
              />
            </TabPanel>
            {/* Then Templates
            <TabPanel p={0}>
              <DeviceTemplate />
            </TabPanel> */}
          </TabPanels>
        </Box>
      </Tabs>
      <Flex
        as="footer"
        position="fixed"
        left={0}
        bottom="0"
        width="100%"
        bg={useColorModeValue('gray.100', 'navy.800')}
        color={useColorModeValue('gray.600', 'gray.200')}
        py={2}
        px={4}
        align="center"
        justify="space-between"
        zIndex={20} // higher than sidebar
        borderTop="1px solid"
        borderColor={useColorModeValue('gray.200', 'gray.700')}
        boxShadow="0 -2px 8px rgba(0,0,0,0.04)"
      >
        <Text fontSize="sm" fontWeight="medium">
          Version 1.0.0
        </Text>
        <Text fontSize="sm" fontWeight="medium">
          UGX Systems
        </Text>
      </Flex>
    </Box>
  );
};

export default EditDevice;
